import React, { useEffect, useMemo, useState } from "react";
import api from "../../../api/axiosConfig";
import "../../../center admin css/centerAdmin.css";

export type SidebarItem = {
  id: string;
  date: string;
  name: string;
  genderShort: "M" | "F";
  ageYears: number;
  avatarUrl?: string;
  time?: string;
  emrId?: string;
  queueStatus?: "WAITING" | "IN_PROGRESS";
  sessionId?: string;
};

type Props = {
  centerId: string;
  selectedDate: string;
  queueApi: string;
  activeId?: string;
  onSelect?: (patient: SidebarItem) => void;
};

function formatDmy(iso?: string) {
  if (!iso) return "";

  const d = new Date(iso);

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);

  return `${dd}/${mm}/${yy}`;
}

function formatHm(iso?: string) {
  if (!iso) return "";

  const d = new Date(iso);

  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");

  return `${hh}:${mm}`;
}

const LabReportQueue: React.FC<Props> = ({
  centerId,
  selectedDate,
  queueApi,
  activeId,
  onSelect,
}) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [items, setItems] = useState<SidebarItem[]>([]);
  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [avatarMap, setAvatarMap] = useState<Record<string, string>>({});

  async function loadQueue() {
    if (!centerId || !selectedDate) return;

    setLoading(true);
    setErr(null);

    try {
      const token = sessionStorage.getItem("accessToken");

      if (!token) {
        throw new Error("No access token found.");
      }

      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const { data } = await api.centerworkflowApi.get(
        `/queues/${queueApi}?centerId=${encodeURIComponent(
          centerId,
        )}&date=${encodeURIComponent(selectedDate)}`,
        {
          headers,
        },
      );

      const rows = [
        ...(data?.waiting || []).map((item: any) => ({
          ...item,
          queueStatus: "WAITING",
        })),

        ...(data?.inProgress || []).map((item: any) => ({
          ...item,
          queueStatus: "IN_PROGRESS",
        })),
      ];

      const mapped: SidebarItem[] = rows.map((item: any) => {
        const ts = item.startedAt || item.sessionStartAt || item.createdAt;

        return {
          id: item._id,

          emrId: item.child?.childId,

          sessionId: item.sessionId,

          date: formatDmy(ts),

          time: formatHm(ts),

          name: item.child?.name || "Unknown",

          genderShort: String(item.child?.gender || "M")
            .toUpperCase()
            .startsWith("F")
            ? "F"
            : "M",

          ageYears: Number(item.child?.age || 0),

          avatarUrl: item.child?.profile,

          queueStatus: item.queueStatus,
        };
      });

      //   setItems(mapped);
      setItems(mapped);

      // ✅ AUTO SELECT FIRST PATIENT
      if (mapped.length > 0) {
        const firstPatient = mapped[0];

        setSelectedPatientId(firstPatient.id);

        onSelect?.(firstPatient);
      }
    } catch (e: any) {
      setErr(e?.message || "Failed to load queue");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadQueue();
  }, [centerId, selectedDate, queueApi]);

  /* ================= PRESIGNED IMAGE FETCH ================= */
  useEffect(() => {
    const fetchImages = async () => {
      const token = sessionStorage.getItem("accessToken");

      if (!token) return;

      const newMap: Record<string, string> = {};

      for (const patient of items) {
        const filePath = patient.avatarUrl;

        if (!filePath) continue;

        try {
          // Already presigned
          if (
            filePath.includes("X-Amz-Signature") ||
            filePath.includes("X-Amz-Credential")
          ) {
            newMap[patient.id] = filePath;
            continue;
          }

          // Remove leading slash if present
          const cleanPath = filePath.startsWith("/")
            ? filePath.slice(1)
            : filePath;

          const parts = cleanPath.split("/");

          if (parts.length < 3) {
            console.warn("Invalid Lab Report profile key:", filePath);
            continue;
          }

          const module = `${parts[0]}/${parts[1]}`;
          const key = parts.slice(2).join("/");

          const response = await api.S3config.get("/view-url", {
            params: {
              module,
              key,
            },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          const signedUrl = response.data?.url || (response as any).url;

          if (signedUrl) {
            newMap[patient.id] = signedUrl;
          }
        } catch (err) {
          console.error(
            `Lab Report image fetch failed for ${patient.name}`,
            err,
          );
        }
      }

      setAvatarMap((prev: Record<string, string>) => ({
        ...prev,
        ...newMap,
      }));
    };

    if (items.length > 0) {
      fetchImages();
    }
  }, [items]);
  /* ========================================================= */

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (!q) return items;

    return items.filter((p) => p.name.toLowerCase().includes(q));
  }, [items, query]);

  return (
    <aside className="gc-queue">
      <div className="gc-queue-header">
        <div className="gc-queue-title">Patient Queue</div>

        <div className="gc-queue-searchwrap">
          <input
            className="gc-queue-search"
            placeholder="Search patients..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="gc-queue-list">
        {loading && <div className="gc-queue-empty">Loading...</div>}

        {err && !loading && <div className="gc-queue-empty">Error: {err}</div>}

        {!loading && !err && filtered.length === 0 && (
          <div className="gc-queue-empty">No patients</div>
        )}

        {filtered.map((p) => {
          //   const active = p.id === activeId;
          const active = p.id === selectedPatientId;

          return (
            <button
              key={p.id}
              className={`
                gc-queue-item
                ${active ? "active" : ""}
                ${
                  p.queueStatus === "WAITING"
                    ? "gc-queue-item-waiting"
                    : "gc-queue-item-progress"
                }
              `}
              onClick={() => {
                setSelectedPatientId(p.id);
                onSelect?.(p);
              }}
            >
              <div className="gc-queue-item-body">
                <div className="gc-queue-avatar">
                  {avatarMap[p.id] ? (
                    <img src={avatarMap[p.id]} alt={p.name} />
                  ) : (
                    <div className="gc-queue-avatar-fallback" />
                  )}
                </div>

                <div className="gc-queue-item-info">
                  <div className="gc-queue-item-date">
                    {p.time ? `${p.time} · ${p.date}` : p.date}
                  </div>

                  <div className="gc-queue-name">{p.name}</div>

                  <div className="gc-queue-sub">
                    {p.genderShort}, {p.ageYears}{" "}
                    {p.ageYears === 1 ? "Year" : "Years"}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </aside>
  );
};

export default LabReportQueue;
